import * as React from "react";
import { useRef, useEffect } from "react";
import { InputFieldState } from "./InputFieldState";

interface TFACodeInputProps {
    state: InputFieldState;
    onChange: (value: string) => void;
    onComplete?: (value: string) => void;
    disabled?: boolean;
}

const TFACodeInput: React.FC<TFACodeInputProps> = ({ state, onChange, onComplete, disabled = false }) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const digits = state.value.toString().padEnd(6, ' ').split('').slice(0, 6);

    useEffect(() => {
        // Focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleChange = (index: number, value: string) => {
        // Only allow digits
        const digit = value.replace(/\D/g, '').slice(-1);

        const newDigits = [...digits];
        newDigits[index] = digit || ' ';
        const newValue = newDigits.join('').trim();

        onChange(newValue);

        // Auto-focus next input
        if (digit && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Call onComplete when all 6 digits are entered
        if (newValue.replace(/\s/g, '').length === 6 && onComplete) {
            onComplete(newValue);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !digits[index].trim() && index > 0) {
            // Move to previous input on backspace if current is empty
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        onChange(pastedData);

        // Focus the next empty input or the last one
        const nextIndex = Math.min(pastedData.length, 5);
        inputRefs.current[nextIndex]?.focus();

        if (pastedData.length === 6 && onComplete) {
            onComplete(pastedData);
        }
    };

    return (
        <div className="tfa-code-input-container">
            <div className="tfa-code-inputs">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                        key={index}
                        ref={(el) => { inputRefs.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digits[index] === ' ' ? '' : digits[index]}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        disabled={disabled}
                        className={`tfa-code-digit ${state.errorMessage ? 'error' : ''}`}
                        aria-label={`Digit ${index + 1}`}
                    />
                ))}
            </div>
            {state.errorMessage && (
                <div className="error-message" style={{ marginTop: '12px' }}>
                    {state.errorMessage}
                </div>
            )}
        </div>
    );
};

export default TFACodeInput;
